// Adapts the shared route evaluator into the runner's navigation seam.
//
// runnerNavigation tracks position as an index into the item list and asks a
// resolver "what's the next index". resolveRoute instead computes the whole
// shown path from the items, the answers and the participant facts, so here we
// find the current item on that path and return the index of the next shown
// item (null at the end). Skip-logic sections are never on the shown path, so
// the runner never lands on one.
//
// `answersByItemId` maps an item id to its answer ({values: [...]} for choices,
// {number} for slider/percentage); build it with answerMapFromItems.

import {resolveRoute} from '../../utils/routing/resolveRoute'

export const makeRouteResolver = (participantFacts = {}) => (
  currentIdx,
  items,
  answersByItemId = {}
) => {
  if (!Array.isArray(items) || !items[currentIdx]) return null
  const {shownItemIds} = resolveRoute(items, answersByItemId, participantFacts)
  const currentId = items[currentIdx].id
  const pos = shownItemIds.indexOf(currentId)
  if (pos < 0 || pos + 1 >= shownItemIds.length) return null
  const nextId = shownItemIds[pos + 1]
  const nextIdx = items.findIndex(it => it.id === nextId)
  return nextIdx >= 0 ? nextIdx : null
}

// The first item to show: sections are never shown, and one at the very start may
// already route on the participant's facts. Falls back to the first item that is
// not a section when the route shows nothing.
export const firstShownIndex = (items, participantFacts = {}) => {
  const [first] = routeShownIndices(items, {}, participantFacts)
  if (first != null) return first
  const fallback = (Array.isArray(items) ? items : []).findIndex(item => item?.kind !== 2)
  return fallback >= 0 ? fallback : 0
}

// The answers to keep when submitting: only those to questions on the route the
// answers now take. An answer given on a path the respondent then backed out of
// is dropped here, as the server would drop it.
export const answersOnRoute = (items, responses = [], participantFacts = {}) => {
  const answers = Array.isArray(responses) ? responses : []
  if (!(Array.isArray(items) && items.some(item => item?.kind === 2))) return answers
  const byQuestionId = {}
  answers.forEach(r => (byQuestionId[r.questionId] = r))
  const answerMap = answerMapFromItems(items, byQuestionId, 'questionId')
  const shown = new Set(routeShownIndices(items, answerMap, participantFacts).map(i => items[i].questionId))
  return answers.filter(r => shown.has(r.questionId))
}

// The shown items as indices into the item list, in order - for the first item
// to show and for the progress count (sections are never shown).
export const routeShownIndices = (
  items,
  answersByItemId = {},
  participantFacts = {}
) => {
  if (!Array.isArray(items)) return []
  const {shownItemIds} = resolveRoute(items, answersByItemId, participantFacts)
  return shownItemIds
    .map(id => items.findIndex(it => it.id === id))
    .filter(idx => idx >= 0)
}

// Slider (1) and percentage (3) answers are a single number in value1; every
// other type carries option values in value1..value10.
const NUMERIC_ENTRY_TYPES = new Set([1, 3])

// Collect the non-null option values an answer carries (value1..value10).
const answerValues = answer => {
  const values = []
  for (let n = 1; n <= 10; n++) {
    const v = answer[`value${n}`]
    if (v != null) values.push(v)
  }
  return values
}

// Build the evaluator's answer map (item id -> {values} or {number}) from the
// respondent's answers, stored under the item id the rules reference. `items` are
// evaluator items (toRouteItems): `id` is the item id and `questionId` the bank
// question. `keyedBy` says which of the two the answers were recorded under - the
// live runner records them by question id, the preview by item id - so an answer
// is never picked up by the other id, which can be the same number for a
// different question.
export const answerMapFromItems = (items, answersByKey = {}, keyedBy = 'id') => {
  const map = {}
  ;(Array.isArray(items) ? items : []).forEach(item => {
    if (item?.id == null || item.kind !== 0) return
    const answer = answersByKey[keyedBy === 'questionId' ? item.questionId : item.id]
    if (!answer) return
    if (NUMERIC_ENTRY_TYPES.has(item.questionType)) {
      if (answer.value1 != null) map[item.id] = {number: answer.value1}
    } else {
      const values = answerValues(answer)
      if (values.length) map[item.id] = {values}
    }
  })
  return map
}
